import {
  Component,
  OnInit,
  AfterViewInit,
  OnDestroy,
  DestroyRef,
  LOCALE_ID,
  ViewChild,
  ElementRef,
  computed,
  inject,
  signal,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from "@angular/core";
import { OverlayModule } from "@angular/cdk/overlay";
import { MatBottomSheet } from "@angular/material/bottom-sheet";
import { Observable } from "rxjs";
import { filter } from "rxjs/operators";
import { MatDrawer } from "@angular/material/sidenav";
import { MatSelect, MatSelectChange } from "@angular/material/select";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { LogoutService } from '@app/core/auth/logout.service';
import { ActivatedRoute, NavigationCancel, NavigationEnd, NavigationError, Router } from "@angular/router";
import { DatabaseCacheService, Section } from "../../../data/cache/database-cache.service";
import { Title } from "@angular/platform-browser";
import { SHARED_IMPORTS } from "src/app/shared/standalone-imports";
import { MentionMenuItemComponent } from "../home-mention-list/mention-menu-item/mention-menu-item.component";
import { HomeNavViewModel, HomeNavViewModelState } from "./home-nav.viewmodel";
import { ThemeMode, ThemeService } from '@app/core/layout/theme.service';
import { AppHeaderComponent } from "src/app/shared/components/app-header/app-header.component";
import { RecentUsersService, RecentUserEntry } from '@app/core/layout/recent-users.service';
import { SecondaryPanelService } from '@app/core/layout/secondary-panel.service';
import { LayoutBreakpointService } from '@app/core/layout/layout-breakpoint.service';
import { UserDetailContextService } from '@app/core/layout/user-detail-context.service';
import { RightDrawerContentComponent } from "../right-drawer-content/right-drawer-content.component";
import { UserQuickSwitchPopoverComponent } from "../user-quick-switch-popover/user-quick-switch-popover.component";
import { UserQuickSwitchBottomSheetComponent } from "../user-quick-switch-bottom-sheet/user-quick-switch-bottom-sheet.component";
import { SectionSwitchBottomSheetComponent, SectionSwitchItem } from "../section-switch-bottom-sheet/section-switch-bottom-sheet.component";

@Component({
  selector: "home-nav",
  templateUrl: "./home-nav.component.html",
  styleUrls: ["./home-nav.component.scss"],
  imports: [...SHARED_IMPORTS, OverlayModule, MentionMenuItemComponent, AppHeaderComponent, RightDrawerContentComponent, UserQuickSwitchPopoverComponent],
  providers: [HomeNavViewModel],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomeNavComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild("logoElement", { static: true }) logoElement?: ElementRef;
  @ViewChild("drawer") drawer?: MatDrawer;
  @ViewChild("sectionSelect") sectionSelect?: MatSelect;

  readonly isHandset$: Observable<boolean>;
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly themeService = inject(ThemeService);
  private readonly isEnglish = (inject(LOCALE_ID) || '').toLowerCase().startsWith('en');
  private readonly recentUsersService = inject(RecentUsersService);
  private readonly secondaryPanelService = inject(SecondaryPanelService);
  private readonly layoutBreakpoint = inject(LayoutBreakpointService);
  readonly userDetailContext = inject(UserDetailContextService);
  private readonly logoutService = inject(LogoutService);
  private readonly databaseCacheService = inject(DatabaseCacheService);
  private readonly bottomSheet = inject(MatBottomSheet);

  private readonly defaultLogoUrl =
    "https://gokuridevitemstest.blob.core.windows.net/section-group-assets/gokuri_logo_bk.svg?sp=r&st=2023-02-13T07:43:06Z&se=3023-02-13T15:43:06Z&spr=https&sv=2021-06-08&sr=b&sig=QBi1qOR7NcuMrHhEg87%2BxmObo%2F5OD64jy3gtz027hq8%3D";

  readonly year = new Date().getFullYear();
  isNavSystemOpen = true;
  toolbarTitle = "";
  logoUrl = this.defaultLogoUrl;

  readonly vm$!: Observable<HomeNavViewModelState>;
  private latestState: HomeNavViewModelState | null = null;

  private static readonly SIDENAV_COLLAPSED_KEY = 'gokuri.sidenavCollapsed';

  // Sidenav collapsed state (for desktop) - restored from localStorage
  readonly isSidenavCollapsed = signal(this.loadSidenavCollapsed());
  private isHandset = false;

  public administeredSectionGroupId: string | null = null;

  // Responsive section selector state
  isSectionSelectExpanded = false;
  private readonly NARROW_WIDTH_BREAKPOINT = 768;

  // Pending timer used to open the section selector panel after the trigger
  // has expanded (mobile only). Tracked so it can be cancelled to avoid the
  // panel re-opening after a selection / close.
  private sectionSelectOpenTimer: ReturnType<typeof setTimeout> | null = null;

  // Selected section ID as signal for reactivity
  private readonly selectedSectionId = signal<string | null>(null);

  // Track if current route is an error page (unauthorized, not-found)
  readonly isErrorRoute = signal(false);

  // Recent Users for current section (reactive)
  readonly recentUsersForSection = computed(() => {
    const sectionId = this.selectedSectionId();
    if (!sectionId) return [];
    return this.recentUsersService.validRecentUsers()
      .filter(u => u.sectionId === sectionId);
  });

  // Signal to track cache updates for reactivity
  private readonly cacheVersion = signal(0);

  // All cached users for current section (converted to RecentUserEntry format)
  readonly cachedUsersForSection = computed(() => {
    // Trigger reactivity when cache updates
    this.cacheVersion();
    const sectionId = this.selectedSectionId();
    if (!sectionId) return [];

    const usersList = this.databaseCacheService.getUsersList();
    if (!usersList || !Array.isArray(usersList)) return [];

    return usersList.map((item: { user: { userId: string; userName: string; sex?: string } }) => ({
      id: item.user.userId,
      name: item.user.userName,
      sectionId: sectionId,
      sex: item.user.sex as 'male' | 'female' | undefined,
      accessedAt: Date.now(), // Not relevant for cached users, but required by interface
    } as RecentUserEntry));
  });
  readonly themeIcon = computed(() => {
    const mode = this.themeService.mode();
    if (mode === "system") return "brightness_auto";
    return mode === "dark" ? "dark_mode" : "light_mode";
  });
  private readonly themeLabelDark = $localize`:@@home.homeNav.theme.switchToDark:ダークモードに切替`;
  private readonly themeLabelSystem = $localize`:@@home.homeNav.theme.switchToSystem:システム設定に切替`;
  private readonly themeLabelLight = $localize`:@@home.homeNav.theme.switchToLight:ライトモードに切替`;
  readonly themeLabel = computed(() => {
    const mode = this.themeService.mode();
    if (mode === "light") return this.themeLabelDark;
    if (mode === "dark") return this.themeLabelSystem;
    return this.themeLabelLight;
  });
  readonly quickActionCreateUserLabel = $localize`:@@home.homeNav.button.createUser:ユーザー作成`;
  readonly quickActionInviteStaffLabel = $localize`:@@home.homeNav.button.inviteStaff:スタッフ招待`;

  /** Whether the secondary panel is currently open */
  readonly isSecondaryPanelOpen = this.secondaryPanelService.isOpen;

  /** Active quick action ID for selected state on menu items */
  readonly activeQuickActionId = this.secondaryPanelService.quickActionId;

  /** User quick switch popover open state */
  readonly isUserPopoverOpen = signal(false);

  /** Hover timers for popover open/close delay */
  private popoverOpenTimer: ReturnType<typeof setTimeout> | null = null;
  private popoverCloseTimer: ReturnType<typeof setTimeout> | null = null;

  /** Width of the secondary panel based on device */
  readonly secondaryPanelWidth = computed(() => {
    if (!this.secondaryPanelService.isOpen()) return '0';
    if (this.layoutBreakpoint.isHandset()) return '100%';
    return this.layoutBreakpoint.isTablet() ? '260px' : '280px';
  });

  /** Whether to apply secondary panel layout (has-secondary-panel class) */
  readonly showSecondaryPanelLayout = this.secondaryPanelService.isOpen;

  constructor(
    public router: Router,
    private route: ActivatedRoute,
    private titleService: Title,
    private destroyRef: DestroyRef,
    private vm: HomeNavViewModel,
  ) {
    this.isHandset$ = this.layoutBreakpoint.isHandset$;
    this.vm$ = this.vm.vm$;
  }

  private updateTitle(sectionGroupName?: string): void {
    if (!sectionGroupName) {
      return;
    }
    this.titleService.setTitle(`${sectionGroupName} Cloud`);
  }

  ngOnInit() {
    // Clean up expired recent users from storage
    this.recentUsersService.cleanupExpired();

    let administeredSectionGroups = this.databaseCacheService.getSectionGroupAdmins();
    if (this.databaseCacheService.hasAllSectionGroupManagement()) {
      this.administeredSectionGroupId = this.databaseCacheService.getSectionGroupDocuments()?.[0]?.id ?? null;
    }
    else{
      if (administeredSectionGroups != null && administeredSectionGroups.length > 0) {
        this.administeredSectionGroupId = administeredSectionGroups[0].sectionGroupId;
      }
    }

    
    // Track if current route is an error page (unauthorized, not-found)
    // Check initial URL
    this.isErrorRoute.set(this.checkIsErrorRoute(this.router.url));
    // Subscribe to route changes and clear loading state on navigation complete
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((event) => {
      this.isErrorRoute.set(this.checkIsErrorRoute(event.urlAfterRedirects));

      // Auto-close mobile drawer on navigation
      if (this.isHandset && this.drawer?.opened) {
        this.drawer.close();
      }

      // Clear loading state when navigation completes
      // Extract sectionId from URL: /home/:sectionId/...
      const match = event.urlAfterRedirects.match(/\/home\/([^\/]+)/);
      const sectionId = match?.[1] ?? null;
      this.vm.onNavigationComplete(sectionId);
    });

    // Clear loading state on navigation cancel/error to prevent stuck skeleton
    this.router.events.pipe(
      filter((event): event is NavigationCancel | NavigationError =>
        event instanceof NavigationCancel || event instanceof NavigationError
      ),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.vm.clearLoadingState();
    });

    // Track handset state
    this.isHandset$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((isHandset) => {
      this.isHandset = isHandset;
    });

    // Auto-collapse sidenav when entering tablet mode
    this.layoutBreakpoint.isTablet$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isTablet) => {
        if (isTablet) {
          this.isSidenavCollapsed.set(true);
        }
      });

    this.vm$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((state) => {
      this.latestState = state;
      this.selectedSectionId.set(state.selectedSection?.sectionId ?? null);
      this.logoUrl = this.normalizeLogo(
        state.selectedSectionGroup?.settings?.assets?.logoUrl,
      );
      this.updateTitle(state.selectedSectionGroup?.name);
      this.toolbarTitle = state.selectedSection?.sectionName ?? "";
      this.cdr.markForCheck();
    });

    // Subscribe to cache updates for user list reactivity
    this.databaseCacheService.parametersUpdated
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.cacheVersion.update(v => v + 1);
      });

    this.route.paramMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        this.vm.setCurrentLocale(params.get("lang"));
        const sectionIdInUrl = params.get("sectionId");
        this.cdr.markForCheck();
        if (sectionIdInUrl) {
          this.vm.setSectionById(sectionIdInUrl);
        }
      });

    this.vm.init();
  }

  ngAfterViewInit(): void {
    this.logoUrl = this.normalizeLogo(
      this.latestState?.selectedSectionGroup?.settings?.assets?.logoUrl,
    );
    this.updateTitle(this.latestState?.selectedSectionGroup?.name);
  }

  ngOnDestroy(): void {
    this.clearPopoverTimers();
    this.cancelSectionSelectOpen();
  }

  reloadInfo(event: MatSelectChange) {
    const nextSection = event.value as Section | null;
    if (!nextSection?.sectionId) {
      return;
    }

    // Pre-update contract status immediately from cached data for smooth UX
    this.vm.preSelectSection(nextSection.sectionId);

    // Start section selection BEFORE navigation so cache is updated when resolver runs
    // This prevents double-call between component and resolver
    // The promise is not awaited - API call runs in background
    this.vm.selectSection(nextSection).catch((error) => {
      console.error('[HomeNavComponent] Failed to select section:', error);
    });

    // Navigate after section selection has started (cache is updated synchronously)
    this.router.navigate(["home", nextSection.sectionId, "users"]);
  }

  async selectSection(section: Section) {
    if (!section?.sectionId) {
      return;
    }

    // Pre-update contract status immediately from cached data for smooth UX
    this.vm.preSelectSection(section.sectionId);

    // Start section selection BEFORE navigation so cache is updated when resolver runs
    // This prevents double-call between component and resolver
    // The promise is not awaited - API call runs in background
    this.vm.selectSection(section).catch((error) => {
      console.error('[HomeNavComponent] Failed to select section:', error);
    });

    // Navigate after section selection has started (cache is updated synchronously)
    this.router.navigate(["home", section.sectionId, "users"]);
  }

  /** Compare sections for mat-select */
  compareSections(a: Section | null, b: Section | null): boolean {
    return a?.sectionId === b?.sectionId;
  }

  /** Handle section selection from mat-select */
  onSectionChange(event: MatSelectChange): void {
    const section = event.value as Section;
    // Cancel any pending programmatic open and force the panel closed so the
    // custom mobile open path can't race Material's close and leave it open.
    this.cancelSectionSelectOpen();
    this.sectionSelect?.close();
    this.isSectionSelectExpanded = false;
    this.selectSection(section);
  }

  onSelectionChange(isOpen: boolean) {
    if (isOpen) {
      this.vm.confirmSelection();
    }
  }

  clickedOption(section: Section) {
    const confirmed = this.latestState?.confirmedSelectedSection;
    if (!confirmed || section.sectionId !== confirmed.sectionId) {
      return;
    }
    this.router.navigate(["home", section.sectionId, "users"]);
  }

  logout() {
    this.logoutService.logout();
  }

  toggleSelf() {
    if (this.isHandset) {
      // Mobile: toggle drawer (overlay mode only)
      this.drawer?.toggle();
    } else {
      // Tablet/Desktop: toggle collapsed state and persist
      this.isSidenavCollapsed.update(v => {
        const next = !v;
        try { localStorage.setItem(HomeNavComponent.SIDENAV_COLLAPSED_KEY, String(next)); } catch {}
        return next;
      });
    }
  }

  public sectionComparison = (option?: Section, value?: Section): boolean =>
    !!option && !!value && option.sectionId === value.sectionId;

  trackSectionById(index: number, section: Section): string {
    return section.sectionId;
  }

  private loadSidenavCollapsed(): boolean {
    try {
      return localStorage.getItem(HomeNavComponent.SIDENAV_COLLAPSED_KEY) === 'true';
    } catch {
      return false;
    }
  }

  private normalizeLogo(logoUrl?: string): string {
    return !logoUrl || logoUrl === "http://" ? this.defaultLogoUrl : logoUrl;
  }

  /** Check if URL is an error page (unauthorized, not-found) */
  private checkIsErrorRoute(url: string): boolean {
    // Use router.parseUrl for robust handling of query params, fragments, and matrix params
    const tree = this.router.parseUrl(url);
    const segments = tree.root.children['primary']?.segments.map(s => s.path) ?? [];
    const lastSegment = segments[segments.length - 1];
    return lastSegment === 'unauthorized' || lastSegment === 'not-found';
  }

  openNewWindow(ruterurl) {
    // Converts the route into a string that can be used
    // with the window.open() function
    const url = this.router.serializeUrl(this.router.createUrlTree([ruterurl]));
    // window.open('#'+url, '_self');
    window.open("#" + url, "_blank");
  }

  callFullscreen() {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.();
      return;
    }
    document.exitFullscreen?.();
  }

  toggleTheme(): void {
    const modes: ThemeMode[] = ["light", "dark", "system"];
    const current = this.themeService.mode();
    const nextIndex = (modes.indexOf(current) + 1) % modes.length;
    this.themeService.setTheme(modes[nextIndex]);
  }

  navigateToCreateUser(): void {
    const state = this.latestState;
    if (state?.selectedSection?.sectionId) {
      this.secondaryPanelService.toggleQuickAction('createUser', state.selectedSection.sectionId);
      // Close sidenav on mobile when opening secondary panel
      if (this.isHandset && this.secondaryPanelService.isOpen()) {
        this.drawer?.close();
      }
    }
  }

  navigateToInvite(): void {
    const state = this.latestState;
    if (state?.selectedSection?.sectionId) {
      this.secondaryPanelService.toggleQuickAction('inviteStaff', state.selectedSection.sectionId);
      // Close sidenav on mobile when opening secondary panel
      if (this.isHandset && this.secondaryPanelService.isOpen()) {
        this.drawer?.close();
      }
    }
  }

  getInitials(name: string): string {
    if (!name) return '';
    return name.split(/\s+/).map(n => n[0]).join('').slice(0, 2).toUpperCase();
  }

  /** Handle mouse enter on users menu item - show popover after delay */
  onUsersMenuMouseEnter(): void {
    // Mobile: ignore hover (use tap instead via onUsersMenuClick)
    if (this.layoutBreakpoint.isHandset()) return;

    this.clearPopoverTimers();
    // Only show popover when on user detail page and there are cached users
    if (this.userDetailContext.isActive() && this.cachedUsersForSection().length > 1) {
      this.popoverOpenTimer = setTimeout(() => {
        this.isUserPopoverOpen.set(true);
      }, 300);
    }
  }

  /** Handle mouse leave on users menu item - close popover after delay */
  onUsersMenuMouseLeave(): void {
    this.clearPopoverTimers();
    this.popoverCloseTimer = setTimeout(() => {
      this.isUserPopoverOpen.set(false);
    }, 200);
  }

  /** Keep popover open when mouse enters it */
  onUserPopoverMouseEnter(): void {
    this.clearPopoverTimers();
  }

  /** Close popover when mouse leaves it */
  onUserPopoverMouseLeave(): void {
    this.clearPopoverTimers();
    this.popoverCloseTimer = setTimeout(() => {
      this.isUserPopoverOpen.set(false);
    }, 200);
  }

  /** Handle user selection from popover */
  onPopoverUserSelected(user: RecentUserEntry): void {
    this.isUserPopoverOpen.set(false);
    // Navigate to the same sub-page as current user detail
    const currentPath = this.router.url;
    const match = currentPath.match(/\/users\/[^/]+\/([^/?#]+)/);
    const subPath = match?.[1] || 'summaries';
    this.router.navigate(['home', user.sectionId, 'users', user.id, subPath]);
  }

  /** Handle click on users menu item - for mobile bottom sheet */
  onUsersMenuClick(event: Event): void {
    // Only handle on mobile when on user detail page
    if (!this.layoutBreakpoint.isHandset()) return;
    if (!this.userDetailContext.isActive()) return;
    if (this.cachedUsersForSection().length <= 1) return;

    // Prevent navigation to users list
    event.preventDefault();
    event.stopPropagation();

    this.openUserSwitchBottomSheet();
  }

  /** Open user switch bottom sheet (mobile) */
  private openUserSwitchBottomSheet(): void {
    const sheetRef = this.bottomSheet.open(UserQuickSwitchBottomSheetComponent, {
      data: {
        users: this.cachedUsersForSection(),
        currentUserId: this.userDetailContext.activeUserId(),
      },
      panelClass: 'user-quick-switch-bottom-sheet-panel',
    });

    sheetRef.afterDismissed().subscribe((user: RecentUserEntry | undefined) => {
      if (user) {
        this.onPopoverUserSelected(user);
      }
    });
  }

  private clearPopoverTimers(): void {
    if (this.popoverOpenTimer) {
      clearTimeout(this.popoverOpenTimer);
      this.popoverOpenTimer = null;
    }
    if (this.popoverCloseTimer) {
      clearTimeout(this.popoverCloseTimer);
      this.popoverCloseTimer = null;
    }
  }

  navigateToRecentUser(user: RecentUserEntry): void {
    this.router.navigate(["home", user.sectionId, "users", user.id, "summaries"]);
  }

  /** Close panel on trigger pointerdown if already open (before mat-select processes click) */
  onTriggerPointerDown(event: PointerEvent): void {
    if (this.sectionSelect?.panelOpen) {
      event.preventDefault();
      event.stopPropagation();
      this.sectionSelect.close();
    }
  }

  /**
   * Expand trigger on mousedown (before click opens the panel)
   * For narrow widths, prevent default and open panel after delay
   * This ensures panel position is calculated after trigger is fully expanded
   */
  onSectionSelectMousedown(event: Event): void {
    // Mobile (handset): use a bottom sheet instead of the mat-select dropdown,
    // which is hard to operate on small screens.
    if (this.isHandset) {
      event.preventDefault();
      event.stopPropagation();
      this.sectionSelect?.close();
      this.openSectionSwitchBottomSheet();
      return;
    }

    if (window.innerWidth <= this.NARROW_WIDTH_BREAKPOINT) {
      // Prevent default mat-select click behavior
      event.preventDefault();
      event.stopPropagation();

      // Expand trigger first
      this.isSectionSelectExpanded = true;

      // Wait for DOM to update, then open panel. Track the timer so a pending
      // open can be cancelled (e.g. on selection) and never stacks.
      this.cancelSectionSelectOpen();
      this.sectionSelectOpenTimer = setTimeout(() => {
        this.sectionSelectOpenTimer = null;
        this.sectionSelect?.open();
      }, 50);
    }
  }

  /** Open section switch bottom sheet (mobile) */
  private openSectionSwitchBottomSheet(): void {
    const state = this.latestState;
    const sections = state?.sections ?? [];
    if (sections.length === 0) return;

    const currentSectionId = state?.selectedSection?.sectionId ?? null;
    const sheetRef = this.bottomSheet.open(SectionSwitchBottomSheetComponent, {
      data: {
        sections: sections.map(s => ({ id: s.sectionId, name: s.sectionName })),
        currentSectionId,
      },
      panelClass: 'section-switch-bottom-sheet-panel',
    });

    sheetRef.afterDismissed().subscribe((item: SectionSwitchItem | undefined) => {
      if (!item || item.id === currentSectionId) return;
      const section = sections.find(s => s.sectionId === item.id);
      if (section) {
        this.selectSection(section);
      }
    });
  }

  /** Cancel any pending programmatic open of the section selector panel */
  private cancelSectionSelectOpen(): void {
    if (this.sectionSelectOpenTimer) {
      clearTimeout(this.sectionSelectOpenTimer);
      this.sectionSelectOpenTimer = null;
    }
  }

  /**
   * Collapse trigger when panel closes
   */
  onSectionSelectClosed(): void {
    this.cancelSectionSelectOpen();
    this.isSectionSelectExpanded = false;
  }
}
